import Environment from './models/Environment';

interface IConfig {
  ENV: Environment;
  STORAGE_ACCOUNT: string;
  IDP: string;
  API: string;
  GROUP_DS: string;
  DASHBOARD_DS: string;
  DESIGN_DS: string;
  REPORT_DS: string;
  MEMBERSHIP_DS: string;
  USER_DS: string;
  GROUP_GQL: string;
  DASHBOARD_GQL: string;
  MEMBERSHIP_GQL: string;
  REPORT_GQL: string;
  USER_GQL: string;
  SITE_ADMIN_DS: string;
  CONTENT_DS: string;
  ASSET_DS: string;
  NOTIFICATION_DS: string;
  ASSESSMENT_DS: string;
  DEV: boolean;
}

const domain = 'bracken.cloud';

class Config implements IConfig {
  private readonly _config: { [env in Environment]: Partial<IConfig> } = {
    local: {
      ENV: 'local',
      STORAGE_ACCOUNT: 'bracken2dev',
      IDP: 'https://localhost:62181',
      API: 'http://localhost:8888',
      GROUP_DS: 'http://localhost:4008',
      DASHBOARD_DS: 'http://localhost:4010',
      DESIGN_DS: 'http://localhost:4009',
      REPORT_DS: 'http://localhost:4007',
      MEMBERSHIP_DS: 'http://localhost:4011',
      USER_DS: 'http://localhost:4012',
      SITE_ADMIN_DS: 'http://localhost:4014',
      CONTENT_DS: 'http://localhost:4015',
      ASSET_DS: 'http://localhost:4016',
      NOTIFICATION_DS: 'http://localhost:4017',
      ASSESSMENT_DS: 'http://localhost:4018',
      DEV: true
    },
    dev: {
      ENV: 'dev',
      STORAGE_ACCOUNT: 'bracken2dev',
      IDP: 'https://identity.bracken.work',
      API: 'https://api.bracken.work',
      GROUP_DS: `https://groupds.dev.${domain}`,
      DASHBOARD_DS: `https://dashboardds.dev.${domain}`,
      DESIGN_DS: `https://designds.dev.${domain}`,
      REPORT_DS: `https://reportds.dev.${domain}`,
      MEMBERSHIP_DS: `https://membershipds.dev.${domain}`,
      USER_DS: `https://userds.dev.${domain}`,
      SITE_ADMIN_DS: `https://siteadminds.dev.${domain}`,
      CONTENT_DS: `https://contentds.dev.${domain}`,
      ASSET_DS: `https://assetds.dev.${domain}`,
      NOTIFICATION_DS: `https://notificationds.dev.${domain}`,
      ASSESSMENT_DS: `https://assessmentds.dev.${domain}`,
      DEV: true
    },
    staging: {
      ENV: 'staging',
      STORAGE_ACCOUNT: 'bracken2staging',
      IDP: 'https://identity.staging.brackenserver.net',
      API: 'https://api.staging.brackenserver.net',
      GROUP_DS: `https://groupds.staging.${domain}`,
      DASHBOARD_DS: `https://dashboardds.staging.${domain}`,
      DESIGN_DS: `https://designds.staging.${domain}`,
      REPORT_DS: `https://reportds.staging.${domain}`,
      MEMBERSHIP_DS: `https://membershipds.staging.${domain}`,
      USER_DS: `https://userds.staging.${domain}`,
      SITE_ADMIN_DS: `https://siteadminds.staging.${domain}`,
      CONTENT_DS: `https://contentds.staging.${domain}`,
      ASSET_DS: `https://assetds.staging.${domain}`,
      NOTIFICATION_DS: `https://notificationds.staging.${domain}`,
      ASSESSMENT_DS: `https://assessmentds.staging.${domain}`,
      DEV: false
    },
    live: {
      ENV: 'live',
      STORAGE_ACCOUNT: 'bracken2live',
      IDP: 'https://identity.brackenlearning.com',
      API: 'https://api.brackenlearning.com',
      GROUP_DS: `https://groupds.${domain}`,
      DASHBOARD_DS: `https://dashboardds.${domain}`,
      DESIGN_DS: `https://designds.${domain}`,
      REPORT_DS: `https://reportds.${domain}`,
      MEMBERSHIP_DS: `https://membershipds.${domain}`,
      USER_DS: `https://userds.${domain}`,
      SITE_ADMIN_DS: `https://siteadminds.${domain}`,
      CONTENT_DS: `https://contentds.${domain}`,
      ASSET_DS: `https://assetds.${domain}`,
      NOTIFICATION_DS: `https://notificationds.${domain}`,
      ASSESSMENT_DS: `https://assessmentds.${domain}`,
      DEV: false
    }
  };

  private readonly GraphQLPath = '/graphql';
  private _ENV: Environment;
  private envConfing: Partial<IConfig>;

  public get ENV(): Environment {
    return this._ENV;
  }
  public set ENV(value: Environment) {
    this._ENV = value;
  }

  public get STORAGE_ACCOUNT(): string {
    return this.envConfing.STORAGE_ACCOUNT;
  }

  public get IDP(): string {
    return this.envConfing.IDP;
  }

  public get API(): string {
    return this.envConfing.API;
  }

  public get GROUP_DS(): string {
    return this.envConfing.GROUP_DS;
  }
  public get DASHBOARD_DS(): string {
    return this.envConfing.DASHBOARD_DS;
  }
  public get DESIGN_DS(): string {
    return this.envConfing.DESIGN_DS;
  }
  public get REPORT_DS(): string {
    return this.envConfing.REPORT_DS;
  }
  public get MEMBERSHIP_DS(): string {
    return this.envConfing.MEMBERSHIP_DS;
  }
  public get USER_DS(): string {
    return this.envConfing.USER_DS;
  }
  public get SITE_ADMIN_DS(): string {
    return this.envConfing.SITE_ADMIN_DS;
  }
  public get CONTENT_DS(): string {
    return this.envConfing.CONTENT_DS;
  }
  public get ASSET_DS(): string {
    return this.envConfing.ASSET_DS;
  }
  public get NOTIFICATION_DS(): string {
    return this.envConfing.NOTIFICATION_DS;
  }
  public get ASSESSMENT_DS(): string {
    return this.envConfing.ASSESSMENT_DS;
  }

  public get GROUP_GQL(): string {
    return `${this.GROUP_DS}${this.GraphQLPath}`;
  }
  public get DASHBOARD_GQL(): string {
    return `${this.DASHBOARD_DS}${this.GraphQLPath}`;
  }
  public get REPORT_GQL(): string {
    return `${this.REPORT_DS}${this.GraphQLPath}`;
  }
  public get MEMBERSHIP_GQL(): string {
    return `${this.MEMBERSHIP_DS}${this.GraphQLPath}`;
  }
  public get MEMBERSHIP_PUBLIC_GQL(): string {
    return `${this.MEMBERSHIP_DS}/graphql_public`;
  }
  public get USER_GQL(): string {
    return `${this.USER_DS}${this.GraphQLPath}`;
  }
  public get SITE_ADMIN_GQL(): string {
    return `${this.SITE_ADMIN_DS}${this.GraphQLPath}`;
  }
  public get CONTENT_GQL(): string {
    return `${this.CONTENT_DS}${this.GraphQLPath}`;
  }
  public get ASSET_GQL(): string {
    return `${this.ASSET_DS}${this.GraphQLPath}`;
  }
  public get NOTIFICATION_GQL(): string {
    return `${this.NOTIFICATION_DS}${this.GraphQLPath}`;
  }
  public get ASSESSMENT_GQL(): string {
    return `${this.ASSESSMENT_DS}${this.GraphQLPath}`;
  }

  public get DEV(): boolean {
    return this.envConfing.DEV;
  }

  constructor() {
    this.setEnv(this.getEnv());
  }

  /** Manually set the environment, e.g. when running as standalone player. */
  public setEnv(env: Environment) {
    this.ENV = env;
    switch (this.ENV) {
      case 'live':
        this.envConfing = this._config.live;
        break;
      case 'staging':
        this.envConfing = this._config.staging;
        break;
      case 'dev':
        this.envConfing = this._config.dev;
        break;
      default:
        this.envConfing = this._config.local;
        break;
    }
  }

  private getEnv(): Environment {
    const host = window.location.host;
    const domain =
      host.indexOf(':') > 0
        ? host.substring(0, host.indexOf(':')) // removing :<port> for local development
        : host;
    if (domain.includes('.local.') || domain.includes('localhost')) {
      return 'local';
    } else if (domain.includes('.dev.')) {
      return 'dev';
    } else if (domain.includes('.staging.')) {
      return 'staging';
    } else {
      return 'live';
    }
  }
}

export default new Config();
